import { useMutation, UseMutationResult } from "@tanstack/react-query"
import axios, { AxiosError } from "axios"
import { IAdminLoginRequest, ILoginProviderRequest, ILoginProviderResponse, ILoginRequest, ILoginResponse } from "@features/auth/domain/auth.interfaces.ts"
import { showNotification } from "@mantine/notifications"
import { API_URL, LocalStorageSession } from "@/shared"

const baseUrl = `${API_URL}/auth`

const AUTH_QUERIES = { LOGIN_MICROSOFT: "LOGIN_MICROSOFT", LOGOUT: "LOGOUT", LOGIN_PROVIDER: "LOGIN_PROVIDER", ADMIN_LOGIN: "ADMIN_LOGIN", IMPERSONATE: "IMPERSONATE", STOP_IMPERSONATION: "STOP_IMPERSONATION" }

/** Login vía Microsoft — exportada como función plana además del hook porque axios-config.ts la necesita fuera de React (interceptor de refresh de token) */
export const loginMicrosoft = async (credentials: ILoginRequest): Promise<ILoginResponse> => {
  const { data } = await axios.post<{ data: ILoginResponse }>(`${baseUrl}/loginMicrosoft`, credentials)
  if (!data) throw new AxiosError("Error en el login")
  return data.data
}

export const useLoginMicrosoft = (): UseMutationResult<ILoginResponse, AxiosError, ILoginRequest> => {
  return useMutation({
    mutationKey: [AUTH_QUERIES.LOGIN_MICROSOFT],
    mutationFn: loginMicrosoft,
    onSuccess: (data) => {
      LocalStorageSession.saveAuthorization(data.user, data.accessToken)
      localStorage.setItem("microsoft_user", JSON.stringify(data.user))
    }
  })
}

export const useLogout = (): UseMutationResult<{ message: string }, AxiosError> => {
  return useMutation({
    mutationKey: [AUTH_QUERIES.LOGOUT],
    mutationFn: async () => {
      const { data } = await axios.post<{ message: string }>(`${baseUrl}/logout`)
      return data
    }
  })
}

export const useLoginProvider = (): UseMutationResult<ILoginProviderResponse, AxiosError, ILoginProviderRequest> => {
  return useMutation({
    mutationKey: [AUTH_QUERIES.LOGIN_PROVIDER],
    mutationFn: async (credentials: ILoginProviderRequest) => {
      const { data } = await axios.post<{ data: ILoginProviderResponse }>(`${API_URL}/auth/loginProvider`, credentials)
      return data.data
    },
    onSuccess: (data) => {
      LocalStorageSession.saveAuthorization(data.user, data.accessToken)
      showNotification({
        title: "Éxito",
        message: "Inicio de sesión exitoso",
        color: "green"
      })
    }
  })
}

/** Login directo sin Microsoft. El backend rechaza esta ruta cuando NODE_ENV=production. */
export const useLoginAdmin = (): UseMutationResult<ILoginResponse, AxiosError, IAdminLoginRequest> => {
  return useMutation({
    mutationKey: [AUTH_QUERIES.ADMIN_LOGIN],
    mutationFn: async (credentials: IAdminLoginRequest) => {
      const { data } = await axios.post<{ data: ILoginResponse }>(`${baseUrl}/admin-login`, credentials)
      return data.data
    },
    onSuccess: (data) => {
      LocalStorageSession.saveAuthorization(data.user, data.accessToken)
    }
  })
}

export const useImpersonate = (): UseMutationResult<ILoginResponse, AxiosError, string> => {
  return useMutation({
    mutationKey: [AUTH_QUERIES.IMPERSONATE],
    mutationFn: async (userId: string) => {
      const res = await axios.post<{ data: ILoginResponse }>(`${baseUrl}/impersonate/${userId}`)
      return res.data.data
    },
    onSuccess: (data) => {
      LocalStorageSession.saveAuthorization(data.user, data.accessToken)
      window.location.href = "/" // Reload to apply new permissions
    }
  })
}

export const useImpersonateExternal = (): UseMutationResult<ILoginResponse, AxiosError, void> => {
  return useMutation({
    mutationKey: [AUTH_QUERIES.IMPERSONATE],
    mutationFn: async () => {
      const res = await axios.post<{ data: ILoginResponse }>(`${baseUrl}/impersonate-external`)
      return res.data.data
    },
    onSuccess: (data) => {
      LocalStorageSession.saveAuthorization(data.user, data.accessToken)
      window.location.href = "/" // Reload to apply new permissions
    }
  })
}

export const useStopImpersonation = (): UseMutationResult<ILoginResponse, AxiosError, void> => {
  return useMutation({
    mutationKey: [AUTH_QUERIES.STOP_IMPERSONATION],
    mutationFn: async () => {
      const res = await axios.post<{ data: ILoginResponse }>(`${baseUrl}/stop-impersonation`)
      return res.data.data
    },
    onSuccess: (data) => {
      LocalStorageSession.saveAuthorization(data.user, data.accessToken)
      window.location.href = "/" // Reload to apply original permissions
    }
  })
}
